import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';

import './App.css';

import { HeaderSwitcher } from './components/layouts/corners/HeaderSwitcher';
import { FooterSwitcher } from './components/layouts/corners/FooterSwitcher';
import PublicHeader from './components/layouts/corners/public/PublicHeader';
import PublicFooter from './components/layouts/corners/public/PublicFooter';
import PublicLandingFooter from './components/layouts/corners/public/PublicLandingFooter';
import PublicLandingHeader from './components/layouts/corners/public/PublicLandingHeader';

import { RouteChangeTracker } from './hooks/utils/EventStateUtils';
import FloatingAlertsColumn from './components/layouts/generic/floating-columns/FloatingAlertsColumn';
import { FloatingLoadingCard } from './components/cards/loading/FloatingLoadingCard';
import { useLoadingCard } from './hooks/generic/useLoadingCard';
import { CLEAN_ROUTES, LANDING_ROUTES } from './config';
import { GetCurrentRoute } from './hooks/pageUtils/GetCurrentRoute';
import { GetSearchParams } from './hooks/pageUtils/GetSearchParams';
import { FloatingButtonsMenu } from './components/layouts/generic/floating-columns/FloatingButtonsMenu';
import ToTopHandButton from './components/buttons/accessibility/ToTopHandButton';
import BecomeFranchiseeButton from './components/buttons/marketing/partners/BecomeFranchiseeButton';
import { GlobalStateProvider } from './GlobalStateProvider';

const Home = lazy(() =>
  import('./pages/home/Home')
);

const Franchises = lazy(() =>
  import('./pages/partner/franchises/Franchises')
);

const FranchisesSuccess = lazy(() =>
  import('./pages/partner/franchises/FranchisesSuccess')
);

const ChooseYourPizza = lazy(() =>
  import('./pages/items/ChooseYourPizza')
);

const OurLocations = lazy(() =>
  import('./pages/items/OurLocations')
);

const MenuFavor = lazy(() =>
  import('./pages/items/MenuFlavor')
);

const MenuLocal= lazy(() =>
  import('./pages/items/MenuLocal')
);

const Category = lazy(() =>
  import('./pages/blog/Category')
);

const Contact = lazy(() =>
  import('./pages/contact/Contact')
);

const Admin = lazy(() =>
  import('./pages/admin/Admin')
);

function App() {
  const { displayLoadingCard } = useLoadingCard();
  const [ normalizedSearchParams, setNormalizedSearchParams ] = useState<Map<string, string>>();
  const [ disableNav, setDisableNav ] = useState<boolean>(false);
  const [ route, setRoute ] = useState<string>("");

  useEffect(() => {
    setDisableNav(
      LANDING_ROUTES.some(item => route.startsWith(item)) 
      && 
      normalizedSearchParams?.get('shownav') !== "true"
      &&
      route.length > 1
    );

  }, [route, normalizedSearchParams]);
  
  return (
    <GlobalStateProvider>
      <div className="App">
        <Router >
          <RouteChangeTracker/>
          <header className="App-header">
            <HeaderSwitcher header={disableNav ? <PublicLandingHeader/> : <PublicHeader/>}/>
          </header>
          <div className="App-body">
            <GetCurrentRoute setRoute={setRoute}/>
            <GetSearchParams setParams={setNormalizedSearchParams}/>
            <FloatingAlertsColumn/>
            { displayLoadingCard && <FloatingLoadingCard title={displayLoadingCard}/> }
            <Suspense 
              fallback={
                <FloatingLoadingCard
                  title="Carregando..."
                />
              }
            >
              <Routes>
                <Route path="/:customPage?" element={<Home />} />
                <Route path="/home/:customPage?" element={<Home />} />
                <Route path="/franquia" element={<Franchises />} />
                <Route path="/franquias" element={<Franchises />} />
                <Route path="/franquiadiblasi" element={<Franchises />} />
                <Route path="/diblasifranquia" element={<Franchises />} />
                <Route path="/franquiasdiblasi" element={<Franchises />} />
                <Route path="/diblasifranquias" element={<Franchises />} />
                <Route path="/seja-um-franqueado" element={<Franchises />} />
                <Route path="/franquias-agradecimento" element={<FranchisesSuccess />} />
                <Route path="/escolha-sua-pizza/:pizzaType?" element={<ChooseYourPizza />} />
                <Route path="/nossas-unidades/:country?/:region?" element={<OurLocations/>}/>
                <Route path="/unidades/:country?/:region?" element={<OurLocations/>}/>
                <Route path="/unidade/:country?/:region?/:local?" element={<MenuLocal />} />
                <Route path="/cardapio-sabor/:pizzaType?/:pizzaTag?" element={<MenuFavor />} />
                <Route path="/categoria/:category?/:pageTag?" element={<Category />} />
                <Route path="/di-blasi-na-midia/:category?/:pageTag?" element={<Category />} />
                <Route path="/contato" element={<Contact />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </Suspense>
          </div>
          <FloatingButtonsMenu
            items={(
                (
                  LANDING_ROUTES.some(item => route.startsWith(item))
                  ||
                  CLEAN_ROUTES.some(item => route.startsWith(item))
                )
                &&
                route.length > 1
              ) ?
              [
                ToTopHandButton()
              ]
              :
              [
                ToTopHandButton(),
                BecomeFranchiseeButton()
              ]
            }
          />
          <footer>
            <FooterSwitcher footer={disableNav ? <PublicLandingFooter/> : <PublicFooter/>}/>
          </footer>
        </Router>
      </div>
    </GlobalStateProvider>
  );
}

export default App;